// Per-route search meta from the artifact. The router's beforeEach appends " | DIGU"
// to meta.title, so titles here are stored without that suffix.
// Canonical URLs, Open Graph, Twitter cards and JSON-LD are written from this file by
// src/router/securityHead.js, and baked into each page's HTML at build time.
import { ROUTES } from './routes';

// The one canonical host (www.digu.one and http:// 301 here; see deploy/nginx).
export const CANONICAL_ORIGIN = 'https://digu.one';

// 1200x630 sharing image (public/images/og/digu-og.png, built by `npm run seo:og`).
export const OG_IMAGE = {
  url: `${CANONICAL_ORIGIN}/images/og/digu-og.png`,
  width: 1200,
  height: 630,
  alt: 'DIGU: Claude Code and AI coding agent security',
};

const SITE = CANONICAL_ORIGIN;

// `breadcrumb` is the page's name in BreadcrumbList JSON-LD (the navbar label where there is one).
const PAGES = {
  home: {
    path: ROUTES.home,
    title: 'Claude Code and AI coding agent security',
    description:
      'DIGU secures Claude Code and the other AI coding agents your engineers use. 32 controls, built in your environment, with evidence your auditors accept.',
  },
  services: {
    path: ROUTES.services,
    breadcrumb: 'Services',
    title: 'Claude Code security services and consulting',
    description:
      'Five fixed-fee engagements to secure Claude Code and other AI coding agents, from a one week assessment to a monthly retainer. No hourly rates.',
  },
  assessment: {
    path: ROUTES.assessment,
    breadcrumb: 'Assessment',
    title: 'Claude Code security assessment in one week',
    description:
      'One week. We score your Claude Code and AI agent usage against 32 controls using your own data, and give you a costed ninety day plan.',
  },
  score: {
    path: ROUTES.score,
    breadcrumb: 'Score yourself',
    title: 'Free AI coding agent security score',
    description:
      'Ten questions, two minutes. Get your profile across five control domains, your floor, and the three fixes that matter most. No call required.',
  },
  roadmap: {
    path: ROUTES.roadmap,
    breadcrumb: 'Maturity Roadmap',
    title: 'AI agent security maturity model, five levels',
    description:
      'Five levels, scored across five control domains. See how many of the 32 controls each level needs, and what each level lets you safely do next.',
  },
  baseline: {
    path: ROUTES.baseline,
    breadcrumb: 'Security Baseline',
    title: 'AI agent security baseline: 32 controls',
    description:
      'The DIGU AI Security Baseline. 32 controls in five domains, each with an implementation and its evidence, mapped to OWASP, NIST AI RMF and ISO/IEC 42001.',
  },
  about: {
    path: ROUTES.about,
    breadcrumb: 'About Us',
    title: 'About DIGU, AI coding agent security engineers',
    description:
      'DIGU is Certified in the Claude Partner Network and is an Okta partner. Certified Claude engineers on every project, and a published control baseline.',
  },
  // DRAFT, pending marketing approval: built only from copy already on the page
  // (src/content/security/customers.js). Replaces the legacy meta, which pitched hiring developers.
  customers: {
    path: ROUTES.customers,
    breadcrumb: 'Customer Stories',
    title: 'Customer Stories',
    description:
      'Real Success, Real Impact. How DIVACS, Anthropic Identity and SharpScribe worked with DIGU, from a global IAM team in 2 weeks to an AI MVP built 40% faster.',
  },
};

const pageMeta = (p) => ({
  title: p.title,
  description: p.description,
  og: { title: `${p.title} | DIGU`, description: p.description, url: SITE + p.path, image: OG_IMAGE.url },
  twitter: { title: `${p.title} | DIGU`, description: p.description, image: OG_IMAGE.url },
  breadcrumb: p.breadcrumb,
});

// Route meta in the shape src/router/index.js beforeEach already reads.
export const seoMeta = (key) => pageMeta(PAGES[key]);

// DRAFT, pending marketing approval. Legacy pages that stay linked from the security site have
// no description (or a title only); their route definitions are legacy and stay unchanged, so
// in security mode src/router/securityHead.js applies these by path instead. Wording comes from
// each page's own visible copy.
export const META_OVERRIDES = {
  [ROUTES.contact]: pageMeta({
    path: ROUTES.contact,
    breadcrumb: 'Contact Us',
    title: 'Contact Us',
    description:
      'Get in touch with DIGU for more information. Reach DIGU, LLC in San Francisco at sales@digu.one or +1 (833) 596 3361, or send us a message from this page.',
  }),
  '/privacy-policy': pageMeta({
    path: '/privacy-policy',
    breadcrumb: 'Privacy Policy',
    title: 'Privacy Policy',
    description:
      'The DIGU Privacy Policy: the types of information DIGU LLC may collect when you visit the website, and how we collect, use, maintain, protect and disclose it.',
  }),
  // Footer label as the title; the legacy description (route meta) without the doubled brand.
  '/grassroot-impact-initative': pageMeta({
    path: '/grassroot-impact-initative',
    breadcrumb: 'Grassroot Impact Initiative',
    title: 'Grassroot Impact Initiative',
    description:
      'Explore DIGU Cares, supporting education, health, and economic growth in Nepal through impactful, sustainable programs.',
  }),
  '/divacs': pageMeta({
    path: '/divacs',
    breadcrumb: 'DIVACS customer story',
    title: 'DIVACS customer story',
    description:
      'With DIGU, DIVACS cut costs by 70% and onboarded top talent in less than 40 days. DIVACS is a B2B SaaS platform that streamlines customer journey management.',
  }),
  '/anthropic-identity': pageMeta({
    path: '/anthropic-identity',
    breadcrumb: 'Anthropic Identity customer story',
    title: 'Anthropic Identity Builds a Global IAM Force',
    description:
      'Anthropic Identity cut hiring time by 75% and built a global IAM team in just 2 weeks. Anthropic Identity is an Identity and Access Management services firm.',
  }),
  '/sharp-scribe': pageMeta({
    path: '/sharp-scribe',
    breadcrumb: 'SharpScribe customer story',
    title: 'SharpScribe Brings AI to the Doctor’s Office',
    description:
      'SharpScribe built its AI MVP 40% faster. Physicians record patient encounters, and conversations become structured clinical notes synced into the EMR platform.',
  }),
};

// Paths whose page is a Service (Service JSON-LD with DIGU as provider).
export const SERVICE_PAGES = { services: ROUTES.services, assessment: ROUTES.assessment };

// Never indexed: thank-you and error pages.
export const NOINDEX_ROUTE_NAMES = ['NotFound', 'successfulSubmission'];
